
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/ui/link";
import { Phone } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type SupportedLanguage = Database["public"]["Enums"]["supported_language"];

interface ConsentFormProps {
  language: SupportedLanguage;
  previousConsent?: {
    studyConsent: boolean;
    dataConsent: boolean;
    phoneNumber: string;
  } | null;
  onContinue: (phoneNumber: string, studyConsent: boolean, dataConsent: boolean) => void;
}

const translations = {
  sv: {
    phoneNumber: "Telefonnummer",
    studyInfo: "Jag har fått muntlig och skriftlig informationen om studien och har haft möjlighet att ställa frågor.",
    documentInfo: "Jag får digital eller utskriven kopia på den skriftliga informationen som jag får behålla.",
    readInfo: "Läs informationen här",
    studyConsent: 'Jag samtycker till att delta i studien: Effekter av "Föräldraskap i Sverige" på förtroendet för socialtjänsten',
    dataConsent: "Jag samtycker till att uppgifter om mig behandlas på det sätt som beskrivs i forskningspersonsinformationen.",
    continue: "Fortsätt till undersökningen",
    previousConsent: "Du har redan gett ditt samtycke"
  },
  en: {
    phoneNumber: "Phone number",
    studyInfo: "I have received verbal and written information about the study and have had the opportunity to ask questions.",
    documentInfo: "I will receive a digital or printed copy of the written information that I can keep.",
    readInfo: "Read the information here",
    studyConsent: 'I consent to participate in the study: Effects of "Parenting in Sweden" on trust in social services',
    dataConsent: "I consent to my information being processed as described in the research participant information.",
    continue: "Continue to survey",
    previousConsent: "You have already given your consent"
  },
  ar: {
    phoneNumber: "رقم الهاتف",
    studyInfo: "لقد تلقيت معلومات شفهية وكتابية عن الدراسة وأتيحت لي الفرصة لطرح الأسئلة.",
    documentInfo: "سأحصل على نسخة رقمية أو مطبوعة من المعلومات المكتوبة التي يمكنني الاحتفاظ بها.",
    readInfo: "اقرأ المعلومات هنا",
    studyConsent: "أوافق على المشاركة في الدراسة: تأثيرات 'الأبوة والأمومة في السويد' على الثقة في الخدمات الاجتماعية",
    dataConsent: "أوافق على معالجة معلوماتي كما هو موضح في معلومات المشارك في البحث.",
    continue: "المتابعة إلى الاستبيان",
    previousConsent: "لقد قدمت موافقتك بالفعل"
  },
  ti: {
    phoneNumber: "ቁጽሪ ስልኪ",
    studyInfo: "ብዛዕባ'ዚ መጽናዕቲ ብቓልን ብጽሑፍን ሓበሬታ ረኺበ ኣለኹ፣ ሕቶታት ንምሕታት'ውን ዕድል ረኺበ ኣለኹ።",
    documentInfo: "ክዕቅቦ ዝኽእል ናይ'ቲ ጽሑፋዊ ሓበሬታ ዲጂታላዊ ወይ ሕትመታዊ ቅዳሕ ክወሃበኒ እዩ።",
    readInfo: "ነቲ ሓበሬታ ኣብዚ ኣንብቦ",
    studyConsent: "ኣብ'ዚ መጽናዕቲ ክሳተፍ ተሰማሚዐ ኣለኹ፦ ጽልዋታት 'ወለድነት ኣብ ሽወደን' ኣብ ምትእምማን ማሕበራዊ ኣገልግሎታት",
    dataConsent: "ሓበሬታይ ከምቲ ኣብ ናይ'ቲ መጽናዕቲ ተሳታፊ ሓበሬታ ተገሊጹ ዘሎ ክካየድ ተሰማሚዐ ኣለኹ።",
    continue: "ናብ'ቲ መጽናዕቲ ቀጽል",
    previousConsent: "ኣቐዲምካ ፍቓድካ ሂብካ ኣለኻ"
  },
  fa: {
    phoneNumber: "شماره تلفن",
    studyInfo: "من اطلاعات شفاهی و کتبی در مورد مطالعه را دریافت کرده‌ام و فرصت پرسیدن سؤالات را داشته‌ام.",
    documentInfo: "من یک نسخه دیجیتال یا چاپی از اطلاعات نوشتاری را دریافت خواهم کرد که می‌توانم نگه دارم.",
    readInfo: "اطلاعات را اینجا بخوانید",
    studyConsent: "من رضایت می‌دهم در مطالعه شرکت کنم: تأثیرات 'فرزندپروری در سوئد' بر اعتماد به خدمات اجتماعی",
    dataConsent: "من رضایت می‌دهم که اطلاعات من همانطور که در اطلاعات شرکت‌کننده در تحقیق توضیح داده شده است، پردازش شود.",
    continue: "ادامه به نظرسنجی",
    previousConsent: "شما قبلاً رضایت خود را اعلام کرده‌اید"
  },
  prs: {
    phoneNumber: "شماره تماس",
    studyInfo: "من معلومات شفاهی و کتبی در مورد مطالعه را دریافت کرده‌ام و فرصت پرسیدن سوالات را داشته‌ام.",
    documentInfo: "من یک نسخه دیجیتال یا چاپی از معلومات نوشتاری را دریافت خواهم کرد که می‌توانم نگه دارم.",
    readInfo: "معلومات را اینجا بخوانید",
    studyConsent: "من رضایت می‌دهم در مطالعه اشتراک کنم: تاثیرات 'والدین در سویدن' بر اعتماد به خدمات اجتماعی",
    dataConsent: "من رضایت می‌دهم که معلومات من طوری که در معلومات اشتراک‌کننده در تحقیق توضیح داده شده است، پردازش شود.",
    continue: "ادامه به سروی",
    previousConsent: "شما قبلاً رضایت خود را داده اید"
  },
  so: {
    phoneNumber: "Lambarka telefoonka",
    studyInfo: "Waxaan helay macluumaad afka ah iyo qoraal ah oo ku saabsan daraasadda waxaanan fursad u helay inaan su'aalo weydiiyo.",
    documentInfo: "Waxaan heli doonaa nuqul dijital ah ama la daabacay oo ah macluumaadka qoran oo aan haysan karo.",
    readInfo: "Halkan ka akhri macluumaadka",
    studyConsent: "Waxaan oggolahay inaan ka qaybqaato daraasadda: Saamaynta 'Waalidnimada Sweden' ee ku aadan kalsoonida adeegyada bulshada",
    dataConsent: "Waxaan oggolahay in macluumaadkayga loo badbadelo sida lagu sharaxay macluumaadka ka-qaybgalaha cilmi-baarista.",
    continue: "U sii wad sahanka",
    previousConsent: "Waan ogolahay mar hore"
  },
  uk: {
    phoneNumber: "Номер телефону",
    studyInfo: "Я отримав усну та письмову інформацію про дослідження та мав можливість поставити запитання.",
    documentInfo: "Я отримаю цифрову або друковану копію письмової інформації, яку я можу зберегти.",
    readInfo: "Прочитайте інформацію тут",
    studyConsent: "Я погоджуюсь взяти участь у дослідженні: Вплив «Батьківства у Швеції» на довіру до соціальних служб",
    dataConsent: "Я погоджуюся з тим, що інформація про мене буде оброблена у спосіб, описаний у відомостях про дослідницьку особу.",
    continue: "Продовжити до опитування",
    previousConsent: "Ви вже дали свою згоду"
  }
};

export const ConsentForm = ({ language, previousConsent, onContinue }: ConsentFormProps) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [studyConsent, setStudyConsent] = useState(false);
  const [dataConsent, setDataConsent] = useState(false);

  // If previous consent exists, use those values
  useEffect(() => {
    if (previousConsent) {
      setPhoneNumber(previousConsent.phoneNumber || "");
      setStudyConsent(previousConsent.studyConsent);
      setDataConsent(previousConsent.dataConsent);
    }
  }, [previousConsent]);

  const t = translations[language];
  const hasPreviousConsent = previousConsent?.studyConsent && previousConsent?.dataConsent;
  
  // Map each language to its corresponding PDF file
  const getPdfPath = (lang: SupportedLanguage) => {
    return `/FPI_${lang}.pdf`;
  };

  return (
    <div className="space-y-4">
      {hasPreviousConsent && (
        <div className="bg-green-50 p-4 rounded border border-green-200 text-green-800 mb-4">
          <p>{t.previousConsent}</p>
        </div>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="phone" className="flex items-center gap-2">
          <Phone className="h-4 w-4" />
          {t.phoneNumber}
        </Label>
        <Input
          id="phone"
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="070-123 45 67"
          dir={language === 'ar' || language === 'fa' || language === 'prs' ? 'rtl' : 'ltr'}
        />
      </div>

      <div className="space-y-4 text-sm" dir={language === 'ar' || language === 'fa' || language === 'prs' ? 'rtl' : 'ltr'}>
        <p>{t.studyInfo}</p>
        <p>
          {t.documentInfo}{" "}
          <Link href={getPdfPath(language)} target="_blank" rel="noopener noreferrer">
            {t.readInfo}
          </Link>
        </p>
        
        <div className="flex items-start space-x-2">
          <input
            type="checkbox"
            id="studyConsent"
            checked={studyConsent}
            onChange={(e) => setStudyConsent(e.target.checked)}
            className="mt-1"
          />
          <Label htmlFor="studyConsent" className="text-sm">
            {t.studyConsent}
          </Label>
        </div>

        <div className="flex items-start space-x-2">
          <input
            type="checkbox"
            id="dataConsent"
            checked={dataConsent}
            onChange={(e) => setDataConsent(e.target.checked)}
            className="mt-1"
          />
          <Label htmlFor="dataConsent" className="text-sm">
            {t.dataConsent}
          </Label>
        </div>
      </div>

      <Button
        onClick={() => onContinue(phoneNumber, studyConsent, dataConsent)}
        disabled={!studyConsent || !dataConsent}
        className="w-full"
      >
        {t.continue}
      </Button>
    </div>
  );
};
